import RoutePaths from "routes/route-paths";
import { GlobalEntityType, RsWorkspaceEntityType, } from "shared/types";
import { OwnershipFilterTypes } from "store/auth/auth.types";
import { getMatchingItems } from "utils/data";
import { checkServerAvailable } from "utils/network";
import { setLastRequestedRoute, setNoConnectionDialogOpen } from "./global.actions";
import { ProcessVariableCategoryTypes, } from "./global.types";
import { navigate } from "shared/navigation";
export const MAX_VISIBLE_TAGS = 5;
export const collapseTagsList = (tags, maxVisible) => {
    const limit = maxVisible || MAX_VISIBLE_TAGS;
    const visibleTags = [...tags].slice(0, limit);
    const extraTags = [...tags].slice(limit);
    const hiddenTagsQuantity = tags.length - limit;
    const moreTag = { name: `+${hiddenTagsQuantity}`, id: 0, isMoreTag: true, persistent: false };
    const collapsedList = [...visibleTags];
    if (hiddenTagsQuantity > 0) {
        collapsedList.push(moreTag);
    }
    return { collapsedList, extraTagsList: extraTags.map(({ name }) => name).join(", ") };
};
export const prepareResult0DParameters = (parameters) => parameters.map((parameter) => {
    const unitsOrder = parameter.units.reduce((acc, unit) => {
        if (unit.id !== parameter.defaultUnits.id) {
            acc.push(unit.id);
        }
        return acc;
    }, [parameter.defaultUnits.id]);
    return { ...parameter, unitsOrder };
});
export const prepareBioreactors = (bioreactors) => [
    {
        id: "",
        name: "Unknown",
        description: "",
        defaultImpeller: null,
        defaultSparger: null,
        image: null,
        permittedImpellers: [],
        permittedSpargers: [],
    },
    ...bioreactors,
];
export const getBioreactorDefaultSpargerOption = (bioreactorsList, bioreactorId) => {
    const bioreactor = bioreactorsList.find((item) => item.id === bioreactorId);
    return bioreactor?.defaultSparger
        ? { value: bioreactor?.defaultSparger.id, label: bioreactor?.defaultSparger.name }
        : null;
};
export const getBioreactorDefaultImpellerOption = (bioreactorsList, bioreactorId) => {
    const bioreactor = bioreactorsList.find((item) => item.id === bioreactorId);
    return bioreactor?.defaultImpeller
        ? { value: bioreactor?.defaultImpeller.id, label: bioreactor?.defaultImpeller.name }
        : null;
};
export const ascendingCompareFunction = (a, b) => (a > b ? -1 : a < b ? 1 : 0);
export const descendingCompareFunction = (a, b) => (b > a ? -1 : b < a ? 1 : 0);
export const getUpdatedSortingDirection = (currentDirection) => {
    if (currentDirection === null) {
        return "desc";
    }
    else {
        return currentDirection === "asc" ? "desc" : "asc";
    }
};
const getIncludedTagsFilterResults = (selectedTags, list) => selectedTags.length > 0
    ? list.filter((listItem) => {
        const listItemTags = listItem.tags.map((tag) => tag.id);
        const hasAllSelectedTags = selectedTags.every((id) => listItemTags.includes(id)) && listItemTags.length >= selectedTags.length;
        return hasAllSelectedTags;
    })
    : list;
const getExcludedTagsFilterResults = (selectedTags, list) => selectedTags.length > 0
    ? list.filter((listItem) => {
        const listItemTags = listItem.tags.map((tag) => tag.id);
        const hasOneOfSelectedTags = selectedTags.some((id) => listItemTags.includes(id));
        return !hasOneOfSelectedTags;
    })
    : list;
export const getSearchResults = (list, query) => {
    const matchingByName = getMatchingItems(list, "name", query);
    const matchingByDescription = getMatchingItems(list, "description", query);
    return Array.from(new Set([...matchingByName, ...matchingByDescription]));
};
export const getListItemsFilteredByOwnership = (list, currentUserInfo, ownershipFilter) => {
    if (ownershipFilter === OwnershipFilterTypes.Own) {
        return list.filter(({ owners }) => owners.some(({ id }) => id === currentUserInfo?.id));
    }
    return list;
};
export const getResultsFilteredByTags = (list, includedTags, excludedTags) => {
    if (includedTags.length === 0 && excludedTags.length === 0) {
        return list;
    }
    const listItemsWithTags = list.filter((listItem) => !!listItem.tags.length);
    const firstFilterResults = getIncludedTagsFilterResults(includedTags, listItemsWithTags);
    const secondFilterResults = getExcludedTagsFilterResults(excludedTags, firstFilterResults);
    return excludedTags.length > 0 ? secondFilterResults : firstFilterResults;
};
export const getEntityTypeTitle = (entityType) => {
    switch (entityType) {
        case GlobalEntityType.Recipe:
            return "Recipe";
        case GlobalEntityType.CellModel:
            return "Cell model";
        case GlobalEntityType.RsWorkspace:
        case GlobalEntityType.PsWorkspace:
            return "Workspace";
        case GlobalEntityType.ProcessData:
            return "Process data";
        case RsWorkspaceEntityType.ResultSet:
            return "Result set";
        case RsWorkspaceEntityType.ResultSetElement:
            return "Result set element";
        case RsWorkspaceEntityType.Simulation:
            return "Simulation";
        default:
            return "";
    }
};
export const getNutrientsProcessVariables = (processVariables) => processVariables?.reduce((acc, { category, variablesList }) => {
    if (category === ProcessVariableCategoryTypes.NutrientsGeneric ||
        category === ProcessVariableCategoryTypes.NutrientsSpecific) {
        return [...acc, ...variablesList];
    }
    else {
        return acc;
    }
}, []) ?? [];
export const findCategoryItem = (categories, searchCb) => categories.reduce((acc, { variablesList = [] }) => {
    const foundedItem = variablesList.find(searchCb);
    if (foundedItem) {
        return foundedItem;
    }
    else {
        return acc;
    }
}, undefined);
export const getSelectedProcessVariable = (processVariables, variableType) => findCategoryItem(processVariables, (item) => item.key === variableType);
export const goBack = async (dispatch) => {
    const isOnline = await checkServerAvailable();
    if (isOnline) {
        navigate(RoutePaths.Home);
    }
    else {
        dispatch(setLastRequestedRoute(RoutePaths.Home));
        dispatch(setNoConnectionDialogOpen(true));
    }
};
export const getProcessVariablesOptions = (processVariables) => processVariables.reduce((acc, processVariable) => {
    const labelOption = {
        value: processVariable.category,
        label: processVariable.categoryName,
        isGroupName: true,
        disabled: true,
        category: processVariable.category,
    };
    const variableOptions = processVariable.variablesList.map((item) => ({
        value: item.key,
        label: item.name,
        units: item.units,
        category: processVariable.category,
    }));
    return [...acc, ...[labelOption, ...variableOptions]];
}, []);
